import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import BuddyCorner from '@/components/common/BuddyCorner';
import { ASSESSMENT_TYPES, createExam, updateExam } from '@/lib/premiumExams';

/**
 * Step one of planning an exam: what it is called and when it runs.
 * Subjects and chapters are chosen later in "Map portion" — keeping them here
 * meant ticking chapters that the mapper never showed again.
 */
export function ExamFormDialog({ open, onOpenChange, exam, student, onSaved }) {
  const editing = !!exam;
  const [form, setForm] = useState({ assessment_type: 'unit_test', name: '', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(exam
      ? { assessment_type: exam.assessment_type, name: exam.name, start_date: (exam.start_date || '').slice(0, 10), end_date: (exam.end_date || '').slice(0, 10) }
      : { assessment_type: 'unit_test', name: '', start_date: '', end_date: '' });
  }, [open, exam]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give the exam a name'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Add the start and end dates'); return; }
    setSaving(true);
    const payload = {
      ...form,
      child_id: student?.student_id,
      // Subjects stay exactly as the mapper left them.
      subjects: (exam?.subjects || []).map(s => ({
        subject: s.subject,
        chapters_selected: s.chapters_selected || [],
        chapters_mapped: s.chapters_mapped || [],
        exam_date: s.exam_date || '',
      })),
    };
    try {
      const saved = editing ? await updateExam(exam.exam_id, payload) : await createExam(payload);
      toast.success(editing ? 'Exam updated' : 'Exam created — now map its portion');
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || 'Could not save the exam');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="exam-form-dialog">
        <DialogTitle className="font-heading text-xl font-bold text-slate-900">
          {editing ? 'Edit exam dates' : 'Add an exam'}
        </DialogTitle>
        <DialogDescription className="text-sm text-slate-500 font-body">
          {student?.name ? `For ${student.name}. ` : ''}Just the name and the dates here. You will pick the subjects and chapters next, under{' '}<span className="font-heading text-slate-700">Map portion</span>.
        </DialogDescription>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-xs font-body text-slate-500">Assessment type</Label>
            <select data-testid="exam-type-select" value={form.assessment_type}
              onChange={e => setForm(f => ({ ...f, assessment_type: e.target.value }))}
              className="w-full mt-1 p-2 rounded-lg border border-slate-200 text-sm font-body">
              {ASSESSMENT_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-body text-slate-500">Exam name</Label>
            <Input data-testid="exam-name-input" value={form.name} placeholder="Half-Yearly · September"
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-lg text-sm" />
          </div>
          <div>
            <Label className="text-xs font-body text-slate-500">Starts</Label>
            <Input data-testid="exam-start-input" type="date" value={form.start_date}
              onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="mt-1 rounded-lg text-sm" />
          </div>
          <div>
            <Label className="text-xs font-body text-slate-500">Ends</Label>
            <Input data-testid="exam-end-input" type="date" value={form.end_date}
              onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="mt-1 rounded-lg text-sm" />
          </div>
        </div>

        <BuddyCorner inline size={56} mood="curious"
          line="Give me the dates first. Then we will pick the subjects and tick the chapters together in Map portion." />

        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="exam-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="exam-save-btn"
            className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            {editing ? 'Save changes' : 'Create exam'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExamFormDialog;
